import { twMerge } from "tailwind-merge";

import { FeedPostImage } from "@/api/posts/types";
import { Nft } from "@/api/nfts/types";
import Link from "next/link";
import config from "@shared/config/client";
import ExternalLinkIcon from "@/assets/icons/link-new-tab.svg";
import { MEDIA_TYPE } from "@/shared/media";
import { VideoArtwork } from "../ArtworkMedia/VideoArtwork";
import { Gallery } from "../Gallery";
import { EndemicImage } from "../Image";

export interface Props {
  images?: FeedPostImage[];
  nft?: Nft;
  className?: string;
  maxNumberOfImages?: number;
  showLinks?: boolean;
  forceLightTheme?: boolean;
}

const FeedPostGallery = ({
  images,
  className,
  nft,
  maxNumberOfImages = 3,
  showLinks = true,
  forceLightTheme,
}: Props) => {
  if (nft?.mediaType === MEDIA_TYPE.VIDEO) {
    return (
      <div
        className={twMerge("flex flex-col gap-4 relative mt-6 mb-6", className)}
      >
        <VideoArtwork video={nft.image} />

        {showLinks && (
          <Link
            href={`${config.url}/nft/${nft.id}`}
            className="text-body-4 text-center mt-0 text-endemicGray-700 endemic:text-primary endemic:opacity-60 flex justify-center"
            target="_blank"
          >
            {nft.name}{" "}
            <ExternalLinkIcon className="fill-endemicGray-700 endemic:fill-primary w-[12px] ml-2 -mt-0.5" />
          </Link>
        )}
      </div>
    );
  }

  return (images && images?.length > 0) || nft ? (
    <div className={twMerge("flex flex-col mt-6 mb-6", className)}>
      <Gallery className={twMerge("flex gap-2 mx-auto w-full mb-4")}>
        {images &&
          images.map((image, i) => {
            return (
              <span
                key={i}
                className={twMerge(
                  "relative cursor-pointer h-[400px] w-full",
                  images.length >= maxNumberOfImages &&
                    "h-[180px] md:h-[200px]",
                  i > maxNumberOfImages - 1 && "hidden",
                )}
                style={{
                  flexBasis: `1/${
                    images.length > maxNumberOfImages
                      ? maxNumberOfImages
                      : images.length
                  }`,
                }}
                data-src={image.imageUrl}
              >
                <EndemicImage
                  src={image.imageUrl}
                  placeholder={image.imagePlaceholder}
                  alt=""
                  fill
                  className="object-cover h-full"
                  sizes="(max-width: 768px) 100vw, 650px"
                  dynamic
                />

                {images.length && (
                  <>
                    {images.length > maxNumberOfImages &&
                      i == maxNumberOfImages - 1 && (
                        <span className="bg-overlay w-full h-full absolute top-0 left-0 flex items-center justify-center text-white text-h3 cursor-pointer">
                          +{images.length - maxNumberOfImages}
                        </span>
                      )}
                  </>
                )}
              </span>
            );
          })}

        {nft?.image && (
          <span
            className={twMerge("relative cursor-pointer w-full block")}
            data-src={nft?.image}
          >
            <EndemicImage
              alt={nft.name}
              src={nft?.image}
              placeholder={nft.placeholder}
              className="object-cover h-full mx-auto"
              width={nft.imageWidth}
              height={nft.imageHeight}
              aspectRatio
            />
          </span>
        )}
      </Gallery>

      {showLinks && nft?.image && (
        <Link
          href={`${config.url}/nft/${nft.id}`}
          className={twMerge(
            "text-body-4 text-center mt-0 text-endemicGray-700 flex justify-center",
            !forceLightTheme && "endemic:text-primary",
          )}
          target="_blank"
        >
          {nft.name}{" "}
          <ExternalLinkIcon className="fill-endemicGray-700 endemic:fill-primary w-[12px] ml-2 -mt-0.5" />
        </Link>
      )}
    </div>
  ) : null;
};

export default FeedPostGallery;
